/**
 * DevToolsPage — Super Admin → More → Dev Tools workspace.
 *
 * Uses the Super Admin chrome (Shield header, no org switcher) — not
 * MainLayout, which is the org-level shell.
 *
 * Tabs:
 *   1. upload-credit-debit         → Existing UploadCreditFilePage (relocated)
 *   2. import-bill-transactions    → New BillTxnImportTab (payments)
 *   3. import-refunds              → New BillTxnImportTab (refunds)
 *
 * URL: /super-admin/dev-tools?tab=<tab-id>
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Shield, Wrench, FileSpreadsheet, ArrowDownUp, RotateCcw, Gauge, ArrowLeft, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { clearAuth, isSuperAdmin } from '../../lib/auth';

import UploadCreditFilePage from '../payments/UploadCreditFilePage';
import BillTxnImportTab from './BillTxnImportTab';

const TAB_DEFS = [
  {
    id: 'upload-credit-debit',
    label: 'Upload Credit / Debit Bank Statement',
    icon: FileSpreadsheet,
    description: 'Bank-statement (.xls / .xlsx) per Partner + Customer. Credits = Funds Pulled, Debits = Vendor Payments.',
  },
  {
    id: 'import-bill-transactions',
    label: 'Import Bill Transactions',
    icon: ArrowDownUp,
    description: 'Bulk import of bill payments — BillPortal / Anchor / Customer / Previous Incumbent. Mirrors the Record-Payment drawer.',
  },
  {
    id: 'import-refunds',
    label: 'Import Refunds',
    icon: RotateCcw,
    description: 'Bulk import of refunds against bills. Recorded as debits in the Credit/Debit report.',
  },
];

export default function DevToolsPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Guard: Super Admin only
  useEffect(() => {
    if (!isSuperAdmin()) navigate('/dashboard');
  }, [navigate]);

  const initial = useMemo(() => {
    const sp = new URLSearchParams(location.search || '');
    const t = sp.get('tab');
    return TAB_DEFS.find((d) => d.id === t)?.id || TAB_DEFS[0].id;
  }, [location.search]);

  const [active, setActive] = useState(initial);

  useEffect(() => {
    setActive(initial);
  }, [initial]);

  const setTab = (id) => {
    setActive(id);
    const sp = new URLSearchParams(location.search || '');
    sp.set('tab', id);
    navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true });
  };

  const activeDef = TAB_DEFS.find((d) => d.id === active) || TAB_DEFS[0];

  return (
    <div className="min-h-screen bg-background" data-testid="dev-tools-page">
      {/* Super Admin chrome (mirrors SuperAdmin.js header) */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Shield className="h-6 w-6" strokeWidth={2} />
            <h1
              className="text-2xl font-black tracking-tighter"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Super Admin
            </h1>
            <span className="text-muted-foreground">/</span>
            <div className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Dev Tools</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={() => navigate('/super-admin')}
              data-testid="back-to-super-admin"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Super Admin
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={() => navigate('/cockpit')}
            >
              <Gauge className="h-4 w-4 mr-2" />
              Cockpit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-none"
              onClick={() => {
                clearAuth();
                window.location.href = '/login';
              }}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Tab strip */}
      <nav className="border-b border-border bg-card" data-testid="dev-tools-tabs">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex items-center gap-1 overflow-x-auto">
            {TAB_DEFS.map((d) => {
              const Icon = d.icon;
              const isActive = active === d.id;
              return (
                <button
                  key={d.id}
                  onClick={() => setTab(d.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                    isActive
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`dev-tools-tab-${d.id}`}
                >
                  <Icon className="h-4 w-4" />
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Active tab content */}
      <main className="container mx-auto px-6 md:px-12 py-6" data-testid={`dev-tools-active-${active}`}>
        {active === 'upload-credit-debit' && <UploadCreditFilePage noLayout={true} />}
        {active === 'import-bill-transactions' && (
          <BillTxnImportTab
            type="payments"
            title="Import Bill Transactions (Payments)"
            subtitle={activeDef.description}
          />
        )}
        {active === 'import-refunds' && (
          <BillTxnImportTab
            type="refunds"
            title="Import Refunds"
            subtitle={activeDef.description}
          />
        )}
      </main>
    </div>
  );
}
